import { SessionStore } from "../../uidispatcherapi/SessionStore";
import { ActionQueueStore } from "../ActionQueueStore";
import * as AggregatorEvent from "./CheckoutAggregatorEvents";
import { CheckoutAggregatorActions } from "../../actions/CheckoutAggregatorActions";
import { findFirstFsdGroupHeader, findAllFsdGroupHeaders } from "../../model/CheckoutManipulation";
import * as ServerRequestsInternal from "../../server/ServerRequestsInternal";
import { trackUiError } from "../../utils/TrackingUtil";
import { checkoutErrorMessageHandlers, ErrorHandler } from "../ErrorHandler";
import { SubmitCheckout } from "./CheckoutAggregatorEvents";
import history from "../../uidispatcherapi/BettyHistoryModule";
import type { ServiceError } from "../BaseTypes";
import type {
	CheckoutCustomer,
	CheckoutData,
	CheckoutHeader,
	CheckoutStatus,
	PaymentType,
} from "../../model/CheckoutData";
import type { CheckoutUserData, SubmitEvent } from "../../model/CheckoutRequests";
import type { PaymentPreauthorization, PaymentPreauthorizationRequestData } from "../CheckoutTypes";
import type { ErrorCodeHandler } from "../ErrorHandler";
import { getBackendLocale } from "../../server/ServerRequestsInternal";
import { CheckoutSteps } from "../../model/CheckoutSteps";
import { isHipayCreditCardPaymentMethod } from "../../model/CheckoutData";
import type { CheckoutStep } from "../../model/CheckoutSteps";
import type { PriceWithDelivery } from "../../model/Prices";
import { TrackingActions } from "../../actions/TrackingActions";
export type CheckoutAggregatorState = {
	checkoutStatus?: CheckoutStatus;
	submitError?: ServiceError;
	isRequestingHipayUrl: boolean;
	preauthorizationData?: PaymentPreauthorization;
};
export class CheckoutAggregatorStoreImpl extends ActionQueueStore<CheckoutData> {
	_state!: CheckoutAggregatorState;
	_errorHandler = new ErrorHandler(checkoutErrorMessageHandlers);

	constructor() {
		super();
		this.resetState();
		this.listenTo(CheckoutAggregatorActions.onLoadCheckout, this.handleLoadCheckout);
		this.listenTo(CheckoutAggregatorActions.onResetState, this.resetState);
	}

	resetState(skipTrigger?: boolean) {
		this.resetQueue();
		this._state = {
			isRequestingHipayUrl: false,
		};
		if (!skipTrigger) {
			this.trigger();
		}
	}

	createHipayRedirect = (hasHipayMainPayment: boolean, canSaveCard: boolean) => {
		const checkoutData = this.getCheckoutData();
		const pricesByPayment = checkoutData?.checkoutPrices.byPayment;
		const paymentSelection = checkoutData?.header.paymentSelection;
		if (!checkoutData || !pricesByPayment || !paymentSelection) return;
		const total = hasHipayMainPayment ? pricesByPayment.pricesMainPayment : pricesByPayment.pricesRemaining;
		if (!total) return;
		const { id: paymentId, pspOrderId } = hasHipayMainPayment
			? paymentSelection.mainPayment
			: (paymentSelection.complementaryPayment ?? {});

		if (!paymentId || !isHipayCreditCardPaymentMethod(paymentId) || pspOrderId) {
			return;
		}

		const legacyPaymentType = {
			paymentAllowance: paymentId.allowance,
			paymentType: paymentId.type,
		};
		this.getHipayRedirectUrl(checkoutData, total, canSaveCard, legacyPaymentType);
	};

	getHipayRedirectUrl(
		checkoutData: CheckoutData,
		total: PriceWithDelivery,
		canSaveCard: boolean,
		realPaymentType: PaymentType,
	) {
		const userData = this.getUserData();
		const totalWithMargin = total.totalAuthorizationAmount;
		if (!totalWithMargin) return;

		const getLandingUrl = (step: CheckoutStep) => {
			return window.location.origin + "/shop/checkout" + step.url + window.location.search;
		};

		const requestData: PaymentPreauthorizationRequestData = {
			checkoutId: checkoutData.id,
			description: checkoutData.context.cartId,
			amount: {
				total: totalWithMargin.amount,
				shipping: total.delivery ? total.delivery.amount : 0,
				tax: total.vat ? total.vat.amount : null,
			},
			currency: totalWithMargin.currency,
			baseReturnUrl: getLandingUrl(CheckoutSteps.lastStep),
			baseErrorUrl: getLandingUrl(CheckoutSteps.Payment),
			country: userData.country,
			language: getBackendLocale(),
			storeId: userData.storeId,
			customerId: userData.customerId,
			additionalId: userData.additionalId,
			city: checkoutData.customerData.city,
			zipcode: checkoutData.customerData.zipcode,
			streetaddress: checkoutData.customerData.street,
			multiUse: canSaveCard,
		};
		this.handlePaymentPreauthorization(requestData, realPaymentType);
	}

	handleLoadCheckout(onError: (arg0: ServiceError) => void) {
		if (this._actionQueueStatus === "LOADING") {
			return;
		}

		this._actionQueueStatus = "LOADING";
		ServerRequestsInternal.loadCheckoutWithAlternativeServiceTypes(
			this.getUserData(),
			this.handleLoadCheckoutSuccess.bind(this),
			this.handleLoadCheckoutError.bind(this, onError),
		);
		this.trigger();
	}

	handleLoadCheckoutSuccess(checkoutData: CheckoutData) {
		if (this._actionQueueStatus !== "LOADING") {
			console.info("status !== LOADING - ignoring load result");
			return;
		}

		this.receivedServerState(checkoutData);
	}

	handleLoadCheckoutError(onError: (arg0: ServiceError) => void, error: ServiceError) {
		const handlers = [
			{
				canHandle: (e) => e.code === "OC-202",
				handle: () => {
					// ignore, we'll just redirect to cart and don't want to show an error
				},
			},
		];
		new ErrorHandler(handlers).onError((next, e) => onError(e), error);
		this._actionQueueStatus = "UNINIT";
		this.trigger();
	}

	handleSubmitCheckout(submitEvent: SubmitEvent, errorHandlers: Array<ErrorCodeHandler> = []) {
		this.queueAction(
			new AggregatorEvent.SubmitCheckout(
				this,
				submitEvent,
				SessionStore.getLocale() || "",
				...[...checkoutErrorMessageHandlers, ...errorHandlers],
			),
		);
	}

	handlePaymentPreauthorization(requestData: PaymentPreauthorizationRequestData, paymentType: PaymentType) {
		if (this._state.isRequestingHipayUrl) return;
		this._state.isRequestingHipayUrl = true;
		this.trigger();
		ServerRequestsInternal.preauthorizePayment(
			requestData,
			paymentType,
			(data) => {
				this.onPreauthorizationSuccess(data);
				this.trigger();
			},
			(error) => {
				this._state.isRequestingHipayUrl = false;
				this.onPreauthorizationFailed(error);
				this.trigger();
			},
		);
		TrackingActions.onPaymentPreauthorization(requestData, paymentType);
	}

	/****************************
	 *      Action Callbacks     *
	 *****************************/
	onPreauthorizationSuccess(data?: PaymentPreauthorization): void {
		if (data) {
			this._state.preauthorizationData = data;
			window.location.replace(data.forwardUrl);
		} else {
			this._state.isRequestingHipayUrl = false;
			trackUiError("CheckoutAggregatorStore", "Empty response during preauthorization.");
			history.push("/checkout/payments/cancel&cid=error");
		}
	}

	onPreauthorizationFailed(error?: ServiceError): void {
		if (error) {
			trackUiError("CheckoutAggregatorStore", "Preauthorization failed: ", error);
			history.push("/checkout/payments/cancel&cid=error");
		}
	}

	/** ******************
	 *      Getters      *
	 *********************/
	getCheckoutData(): CheckoutData | null | undefined {
		return this._clientState;
	}

	getCustomerData(): CheckoutCustomer | null | undefined {
		return this._clientState?.customerData;
	}

	getHeader(): CheckoutHeader | null | undefined {
		return this._clientState?.header;
	}

	getUserData(): CheckoutUserData {
		return {
			additionalId: SessionStore.isEmployee() ? SessionStore.getEmployeeId() : SessionStore.getCardholderNumber(),
			country: window.country,
			customerId: SessionStore.getCustomerId(),
			storeId: SessionStore.getSelectedStoreId(),
		};
	}

	getFirstFsdGroupHeader() {
		return this._clientState && findFirstFsdGroupHeader(this._clientState);
	}

	getAllFsdGroupHeaders() {
		return this._clientState && findAllFsdGroupHeaders(this._clientState);
	}

	getPreauthorizationData(): PaymentPreauthorization | null | undefined {
		return this._state.preauthorizationData;
	}

	getSubmitError(): ServiceError | null | undefined {
		return this._state.submitError;
	}

	getCheckoutStatus(): CheckoutStatus | null | undefined {
		return this._state.checkoutStatus;
	}

	isSubmitting(): boolean {
		return this._queue.items().some((x) => x instanceof SubmitCheckout);
	}

	isRequestingHipayUrl(): boolean {
		return !!this._state.isRequestingHipayUrl;
	}
}
export const CheckoutAggregatorStore = new CheckoutAggregatorStoreImpl();
